import type * as React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { type CompanyId, MAX_COMPANIES } from '../data/company-data'

interface CompanyContextValue {
  selected: CompanyId[]
  /** Adds or removes a company. Returns false if at the max. */
  toggleCompany: (id: CompanyId) => boolean
  isSelected: (id: CompanyId) => boolean
}

const CompanyContext = createContext<CompanyContextValue | null>(null)

const STORAGE_KEY = 'prep-target-companies'

function readStored(): CompanyId[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.slice(0, MAX_COMPANIES)
  } catch {
    return []
  }
}

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  // Mock persistence: production would store this per-user via the platform API.
  const [selected, setSelected] = useState<CompanyId[]>([])
  const hydratedRef = useRef(false)

  useEffect(() => {
    setSelected(readStored())
    hydratedRef.current = true
  }, [])

  useEffect(() => {
    if (!hydratedRef.current) return
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selected))
    } catch {
      // Ignore storage failures (private mode, quota)
    }
  }, [selected])

  const toggleCompany = useCallback(
    (id: CompanyId) => {
      if (selected.includes(id)) {
        setSelected((prev) => prev.filter((c) => c !== id))
        return true
      }
      if (selected.length >= MAX_COMPANIES) return false
      setSelected((prev) => [...prev, id])
      return true
    },
    [selected],
  )

  const isSelected = useCallback(
    (id: CompanyId) => selected.includes(id),
    [selected],
  )

  const value = useMemo(
    () => ({ selected, toggleCompany, isSelected }),
    [selected, toggleCompany, isSelected],
  )

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  )
}

export function useCompanies() {
  const ctx = useContext(CompanyContext)
  if (!ctx) throw new Error('useCompanies must be used within CompanyProvider')
  return ctx
}
